import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Link, 
  Divider, 
  Avatar, 
  Container, 
  Paper, 
  CircularProgress,
  Grid,
  Skeleton
} from '@mui/material';
import { 
  Email, 
  Phone,
  Business,
  CalendarToday
} from '@mui/icons-material';
import HomeNavbar from '../components/home-nav-bar';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const AuthorProfile = () => {
  const query = useQuery();
  const authorId = query.get('id');
  const authorName = query.get('name');

  const [authorData, setAuthorData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAuthorData = async () => {
      try {
        const response = await axios.get(
          `https://api.wajls.com/client/v1/get_author.php?author_id=${authorId}`
        );
        
        if (response.data.success) {
          setAuthorData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch author data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authorId) fetchAuthorData();
  }, [authorId]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
          <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={150} height={150} />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="50%" />
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="90%" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="90%" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="90%" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" width="90%" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ my: 4, borderColor: '#e0e0e0' }} />
        <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
        {[...Array(3)].map((_, idx) => (
          <Box key={idx} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="50%" />
          </Box>
        ))}
      </Container>
    );
  }
  
  if (!authorData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            The author profile you're looking for doesn't exist or has been removed.
          </Typography>
      </Container>
    );
  }

  const { author, manuscripts } = authorData;
  
  // Only show published manuscripts
  const publishedArticles = manuscripts.filter(m => m.status === 1);
  
  // Generate avatar letters from author name (used as fallback if image fails to load)
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box>
      <HomeNavbar/>
      <Box 
        sx={{ 
          maxWidth: 'lg',
          margin: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Author Details Section */}
        <Box sx={{ mb: 4 }}>
          <Grid 
            container 
            spacing={4} 
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
          >
            <Grid 
              item 
              xs={12} 
              sm={4} 
              md={3} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                order: { xs: 1, sm: 1 }
              }}
            >
              <Avatar 
                src={author.profile_image}
                alt={author.name}
                sx={{ 
                  width: { xs: 150, md: 180 }, 
                  height: { xs: 150, md: 180 }, 
                  fontSize: { xs: 50, md: 60 },
                  bgcolor: '#4a6da7',
                  border: '4px solid #e8f0ff',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                }}
              >
                {getInitials(author.name)}
              </Avatar>
            </Grid>
            
            <Grid 
              item 
              xs={12} 
              sm={8} 
              md={9}
              sx={{ 
                order: { xs: 2, sm: 2 },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              <Typography 
                variant="h4" 
                fontWeight={600} 
                gutterBottom
                sx={{ 
                  textAlign: { xs: 'center', sm: 'left' },
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  color: '#1a3b66',
                  mb: 2
                }}
              >
                {author.name}
              </Typography>
              
              {author.bio && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2.5,
                    textAlign: { xs: 'center', sm: 'left' },
                    color: '#444',
                    lineHeight: 1.6
                  }}
                >
                  {author.bio}
                </Typography>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {author.institution && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: { xs: 'center', sm: 'flex-start' } 
                      }}>
                        <Business sx={{ mr: 1.5 }} />
                        <Typography variant="body2" sx={{ color: '#333' }}>
                          {author.institution}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: { xs: 'center', sm: 'flex-start' } 
                    }}>
                      <CalendarToday sx={{ mr: 1.5 }} />
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        Joined {formatDate(author.sign_up_date)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: { xs: 'center', sm: 'flex-start' } 
                    }}>
                      <Email sx={{ mr: 1.5 }} />
                      <Link 
                        href={`mailto:${author.email}`}
                        underline="hover"
                        sx={{ 
                          textDecoration: 'underline',
                          fontWeight: 500,
                          transition: 'color 0.2s',
                        }}
                      >
                        {author.email}
                      </Link>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: { xs: 'center', sm: 'flex-start' } 
                    }}>
                      <Phone sx={{ mr: 1.5 }} />
                      <Link 
                        href={`tel:${author.phone_number}`}
                        underline="hover"
                        sx={{ 
                          textDecoration: 'underline',
                          fontWeight: 500,
                          transition: 'color 0.2s',
                        }}
                      >
                        {author.phone_number}
                      </Link>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ mb: 4, borderColor: '#e0e0e0' }} />
        
        {/* Articles Section */}
        <Box>
          <Typography 
            variant="h5" 
            fontWeight={600} 
            sx={{ 
              mb: 3,
              color: '#1a3b66',
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: '#4a6da7',
                borderRadius: 1
              }
            }}
          >
            Articles
          </Typography>
          
          {publishedArticles.length === 0 ? (
            <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic', py: 2 }}>
              This author has no published articles yet.
            </Typography>
          ) : (
            <Box sx={{ mt: 4 }}>
              {publishedArticles.map((article, index) => (
                <Box
                  key={article.id}
                  sx={{
                    mb: 2.5,
                    pb: 2,
                    borderBottom: index < publishedArticles.length - 1 ? "1px solid #eaeef5" : "none",
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateX(5px)'
                    }
                  }}
                >
                  <Link
                    href="#"
                    underline="hover"
                    sx={{ 
                      fontWeight: 600, 
                      display: 'block', 
                      fontSize: '1.05rem',
                      color: '#1976d2',
                      mb: 0.75
                    }}
                  >
                    {article.title}
                  </Link>

                  <Typography variant="body2" sx={{ mt: 0.5, color: '#555' }}>
                    Authors:{' '}
                    {article.authors.map((author, i) => (
                      <Link
                        key={author.id}
                        href={`/author?id=${author.id}&name=${encodeURIComponent(author.name)}`}
                        underline="hover"
                        sx={{ 
                          mx: 0.5, 
                          cursor: 'pointer',
                          color: '#2962ff'
                        }}
                        // target="_blank"
                        // target="_blank"
                      >
                        {author.name}
                        {i < article.authors.length - 1 && ','}
                      </Link>
                    ))}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AuthorProfile;